import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { googleCallbackStatusSchema, type GoogleCallbackStatus } from "@nh/shared";
import { Alert, AuthCard, AuthLayout, Button, PulseLoader } from "@/shared/ui/core";
import { useRunOnce } from "@/shared/lib/useRunOnce";
import { useAuth } from "./auth.context";
import { authErrorMessage } from "./auth.errors";
import { DEFAULT_DESTINATION } from "./auth.routes";

/** Qué decirle a la persona por cada estado que puede devolver el backend. */
const STATUS_MESSAGES: Record<Exclude<GoogleCallbackStatus, "success">, string> = {
  denied: "Cancelaste el acceso con Google. Puedes intentarlo otra vez o entrar con tu correo.",
  email_taken:
    "Ese correo ya tiene una cuenta creada con contraseña. Entra con tu correo y contraseña.",
  error: "Google no pudo completar el acceso. Inténtalo de nuevo en un momento.",
};

type Phase = "working" | "failed";

/**
 * Aterrizaje del flujo de Google: `/auth/google?status=...`
 *
 * Google NO devuelve el token por la URL. El backend deja la cookie
 * httpOnly de refresh y redirige aquí con un `status`; si es `success`,
 * esta pantalla pide el access token con un refresh. Así ningún token
 * pasa por la barra de direcciones, donde quedaría en el historial del
 * navegador y en los logs de cualquier proxy del camino.
 */
export function GoogleCallbackPage() {
  const [searchParams] = useSearchParams();
  const { restore } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>("working");
  const [message, setMessage] = useState<string | null>(null);

  /*
   * El intercambio corre UNA vez, aunque StrictMode monte el efecto dos.
   *
   * Aquí vivía un fallo que dejaba esta pantalla en "Conectando…" para
   * siempre: al candado `started` se le sumaba una bandera `alive` con
   * cleanup, y en StrictMode las dos se anulaban —el desmontaje de prueba
   * apagaba la bandera y el segundo montaje salía temprano por el candado,
   * así que cuando `restore()` resolvía ya no quedaba nadie escuchando y
   * nunca se navegaba—. `useRunOnce` es ese candado, sin la mitad que
   * sobraba y con la trampa documentada; el mismo error ya se había
   * cometido en `auth.context.tsx`.
   */
  useRunOnce(() => {
    const parsed = googleCallbackStatusSchema.safeParse(searchParams.get("status"));

    // Un `status` ausente o desconocido se trata como error: llegar aquí
    // sin estado significa que alguien abrió la URL a mano o que el
    // backend cambió el contrato.
    if (!parsed.success) {
      setPhase("failed");
      setMessage(STATUS_MESSAGES.error);
      return;
    }

    if (parsed.data !== "success") {
      setPhase("failed");
      setMessage(STATUS_MESSAGES[parsed.data]);
      return;
    }

    restore()
      .then(() => navigate(DEFAULT_DESTINATION, { replace: true }))
      .catch((error: unknown) => {
        setPhase("failed");
        setMessage(authErrorMessage(error));
      });
  });

  return (
    <AuthLayout width="sm">
      <AuthCard
        title={phase === "working" ? "Conectando…" : "No pudimos entrar"}
        subtitle={
          phase === "working" ? "Estamos terminando tu acceso con Google." : undefined
        }
      >
        {phase === "working" ? (
          <PulseLoader label="Conectando con Google…" className="py-4" />
        ) : (
          <div className="grid gap-4">
            <Alert variant="error">{message}</Alert>
            <Button
              variant="primary"
              size="lg"
              shape="rounded"
              fullWidth
              onClick={() => navigate("/login", { replace: true })}
            >
              Volver al acceso
            </Button>
          </div>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
